import { useCallback, useMemo } from 'react';

import {
  RECOMMENDED_LIABILITY_LIMIT_NUM,
  parseLiabilityLimitValue,
} from '../constants/allCoveragesOptions';
import { CONSENT_TYPE_LIABILITY_LIMIT_REDUCTION } from '../constants';
import { useCoverageConsentConfirmation } from './useCoverageConsentConfirmation';

interface LiabilityPendingData {
  currentLimit: string;
  newLimit: string;
}

interface UseLiabilityLimitConfirmationOptions {
  quoteId: number;
  vehicleVin: string;
  vehicleId: number;
  currentLiabilityCode: string | undefined;
  getLabel: (code: string, field: 'liability_limit_cad') => string;
  isOtherDeductible: (code: string, field: 'liability_limit_cad') => boolean;
  onApplyChange: (newCode: string, clearOther: boolean) => void;
}

export const useLiabilityLimitConfirmation = ({
  quoteId,
  vehicleVin,
  vehicleId,
  currentLiabilityCode,
  getLabel,
  isOtherDeductible,
  onApplyChange,
}: UseLiabilityLimitConfirmationOptions) => {
  const shouldClearOther = useCallback(
    (newCode: string) => !isOtherDeductible(newCode, 'liability_limit_cad'),
    [isOtherDeductible],
  );

  const getPendingData = useCallback(
    (newCode: string, currentCode: string): LiabilityPendingData => {
      const currentLabel = currentCode
        ? getLabel(currentCode, 'liability_limit_cad')
        : '$2,000,000';
      const newLabel = newCode ? getLabel(newCode, 'liability_limit_cad') : '';
      return { currentLimit: currentLabel, newLimit: newLabel };
    },
    [getLabel],
  );

  const {
    requestConfirmation,
    applyChangeDirectly,
    resetState,
    modalProps: baseModalProps,
  } = useCoverageConsentConfirmation<LiabilityPendingData>({
    quoteId,
    vehicleVin,
    vehicleId,
    consentType: CONSENT_TYPE_LIABILITY_LIMIT_REDUCTION,
    onApplyChange,
    shouldClearOther,
    getPendingData,
  });

  const handleLiabilityLimitChange = useCallback(
    (newCode: string) => {
      const currentCode = currentLiabilityCode || '';
      const currentLabel = currentCode
        ? getLabel(currentCode, 'liability_limit_cad')
        : '';
      const newLabel = newCode ? getLabel(newCode, 'liability_limit_cad') : '';

      const currentValue = parseLiabilityLimitValue(currentLabel);
      const newValue = parseLiabilityLimitValue(newLabel);

      const isCurrentAtOrAboveRecommended =
        currentValue === null ||
        currentValue >= RECOMMENDED_LIABILITY_LIMIT_NUM;
      const isNewBelowRecommended =
        newValue !== null && newValue < RECOMMENDED_LIABILITY_LIMIT_NUM;

      if (isCurrentAtOrAboveRecommended && isNewBelowRecommended) {
        requestConfirmation(newCode, currentCode);
      } else {
        applyChangeDirectly(newCode);
      }
    },
    [currentLiabilityCode, getLabel, requestConfirmation, applyChangeDirectly],
  );

  const modalProps = useMemo(
    () => ({
      isOpen: baseModalProps.isOpen,
      onClose: baseModalProps.onClose,
      onConfirm: baseModalProps.onConfirm,
      isLoading: baseModalProps.isLoading,
      currentLimit: baseModalProps.pendingData?.currentLimit || '$2,000,000',
      newLimit: baseModalProps.pendingData?.newLimit || '',
    }),
    [baseModalProps],
  );

  return {
    handleLiabilityLimitChange,
    resetState,
    modalProps,
  };
};
